import { useEffect, useRef } from "react";
import { hasSignedFlkDocument } from "../utils/flkForm";

interface UseSkipDualFormProps {
    updateFormData: any[];
    flkState: {
        signingUrl: string;
        isSigned: boolean;
    };
    formRef: React.MutableRefObject<any[]>;
}

/** How long to wait for Formik to mount before auto-submitting. */
const MOUNT_GRACE_MS = 250;

/** Give up after this long rather than submitting a form that never initialised. */
const MOUNT_TIMEOUT_MS = 6000;

/**
 * Auto-submits a form configured with `skipDualForm`, so the candidate goes
 * straight to the FLK signing step instead of seeing the input form first.
 *
 * Two things this has to get right:
 *
 * 1. Wait for Formik to actually be ready. A flat timer guessed at it, and on a
 *    slow device could submit before the form had its initial values.
 * 2. Re-arm. The latch is keyed by form index, and once tripped it never reset —
 *    so after a candidate used Edit, the auto-submit never fired again and they
 *    were left on a form that is meant to be invisible with nothing to click.
 */
const useSkipDualForm = ({ updateFormData, flkState, formRef }: UseSkipDualFormProps) => {
    const processedRef = useRef<{ [key: number]: boolean }>({});

    useEffect(() => {
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const cleanup = () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
        };

        const activeForm = updateFormData.find((f: any) => f.active);

        if (!activeForm?.flk_config?.skipDualForm) {
            return cleanup;
        }

        const formIndex = activeForm.index;

        // Never auto-submit a form whose document is already signed.
        //
        // Submitting a signed form makes the backend advance to the next page,
        // which is correct when the candidate pressed continue — and completely
        // wrong here. Without this, navigating back to a signed skipDualForm form
        // re-submits it and bounces the candidate forward again, so they can never
        // return to it.
        if (hasSignedFlkDocument(activeForm)) {
            processedRef.current[formIndex] = true;
            return cleanup;
        }

        // A signing URL means this form has already been submitted and is waiting
        // to be signed. Release the latch so that if the candidate edits it and the
        // URL is cleared, it can be submitted again.
        if (flkState.signingUrl !== "") {
            processedRef.current[formIndex] = false;
            return cleanup;
        }

        if (processedRef.current[formIndex]) {
            return cleanup;
        }

        processedRef.current[formIndex] = true;

        const startedAt = Date.now();

        const submitWhenReady = () => {
            if (cancelled) { return; }

            const formInstance = formRef.current[formIndex];

            if (formInstance?.submitForm) {
                formInstance.submitForm();
                return;
            }

            if (Date.now() - startedAt > MOUNT_TIMEOUT_MS) {
                console.warn("FLK: skipDualForm auto-submit gave up waiting for the form", {
                    formIndex,
                    label: activeForm.label,
                });
                // Let the candidate submit it themselves rather than leaving them stuck.
                processedRef.current[formIndex] = false;
                return;
            }

            timer = setTimeout(submitWhenReady, MOUNT_GRACE_MS);
        };

        timer = setTimeout(submitWhenReady, MOUNT_GRACE_MS);

        return cleanup;
    }, [updateFormData, flkState.signingUrl, formRef]);
};

export default useSkipDualForm;
